(() => {
  const PAGE_SELECTOR = 'body.is-index [data-hs-page]';
  const HEADER_SELECTOR = '.c-header';
  const MENU_TOGGLE_SELECTOR = '[data-menu-toggle]';
  const MENU_DRAWER_SELECTOR = '[data-menu-drawer]';
  const MENU_CLOSE_SELECTOR = '[data-menu-close]';
  const PAGE_TOP_LINK_SELECTOR = 'a[href="#pagetop"]';
  const PERSPECTIVE_STAGE_SELECTOR = '[data-perspective-stage]';
  const FIXED_DISABLE_MEDIA_QUERY = '(min-width: 769px) and (max-aspect-ratio: 1440/800)';
  const PERSPECTIVE_STAGE_SOURCE_SELECTORS = [
    '.p-perspective--01',
    '.p-perspective--02',
    '.p-perspective--03',
    '.p-perspective--04'
  ];
  const PERSPECTIVE_STAGE_START_SECTION_INDEX = 0;
  const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const isPerspectiveFixedDisabled = () => window.matchMedia(FIXED_DISABLE_MEDIA_QUERY).matches;
  const isMobileLayout = () => window.matchMedia(MOBILE_MEDIA_QUERY).matches;

  const parseHexColor = (value) => {
    const hex = value.trim().replace('#', '');
    if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return null;

    const normalized = hex.length === 3
      ? hex.split('').map((char) => char + char).join('')
      : hex;

    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16)
    };
  };

  const mixColor = (from, to, ratio) => ({
    r: Math.round(from.r + (to.r - from.r) * ratio),
    g: Math.round(from.g + (to.g - from.g) * ratio),
    b: Math.round(from.b + (to.b - from.b) * ratio)
  });

  const easeInOutSine = (value) => {
    const progress = clamp(value, 0, 1);
    return -(Math.cos(Math.PI * progress) - 1) / 2;
  };

  const toRgbString = ({ r, g, b }) => `rgb(${r} ${g} ${b})`;

  const setupWheelHorizontal = (page) => {
    page.addEventListener(
      'wheel',
      (event) => {
        if (isMobileLayout()) return;

        const mostlyVertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
        if (!mostlyVertical) return;

        const maxLeft = page.scrollWidth - page.clientWidth;
        const nextLeft = Math.min(maxLeft, Math.max(0, page.scrollLeft + event.deltaY));

        if (nextLeft !== page.scrollLeft) {
          event.preventDefault();
          page.scrollLeft = nextLeft;
        }
      },
      { passive: false }
    );
  };

  const setupTouchVerticalHorizontal = (page) => {
    const TOUCH_SCROLL_MULTIPLIER = 1.35;
    const HORIZONTAL_SCROLL_MULTIPLIER = 1;
    const INERTIA_FRICTION = 0.9;
    const INERTIA_STOP_THRESHOLD = 0.2;
    const MAX_TOUCH_STEP = 24;
    const GESTURE_START_THRESHOLD = 6;
    const VERTICAL_GESTURE_RATIO = 1.12;
    const VELOCITY_SMOOTHING = 0.3;

    let isTracking = false;
    let gestureAxis = '';
    let startX = 0;
    let startY = 0;
    let lastX = 0;
    let lastY = 0;
    let inertiaVelocity = 0;
    let inertiaRafId = 0;
    let lastInertiaFrame = 0;

    const stopInertia = () => {
      if (!inertiaRafId) return;
      window.cancelAnimationFrame(inertiaRafId);
      inertiaRafId = 0;
      lastInertiaFrame = 0;
    };

    const runInertia = (timestamp) => {
      if (!lastInertiaFrame) {
        lastInertiaFrame = timestamp;
      }
      const frameScale = clamp((timestamp - lastInertiaFrame) / 16.67, 0.7, 2.2);
      lastInertiaFrame = timestamp;

      if (Math.abs(inertiaVelocity) < INERTIA_STOP_THRESHOLD) {
        stopInertia();
        inertiaVelocity = 0;
        return;
      }

      const maxLeft = page.scrollWidth - page.clientWidth;
      const nextLeft = Math.min(maxLeft, Math.max(0, page.scrollLeft + inertiaVelocity * frameScale));
      const reachedEdge = nextLeft === page.scrollLeft;

      if (reachedEdge) {
        stopInertia();
        inertiaVelocity = 0;
        return;
      }

      page.scrollLeft = nextLeft;
      inertiaVelocity *= Math.pow(INERTIA_FRICTION, frameScale);
      inertiaRafId = window.requestAnimationFrame(runInertia);
    };

    const shouldIgnoreTarget = (target) => target.closest(
      'a, button, input, textarea, select, label, [data-menu-drawer], [data-menu-toggle], [data-menu-close]'
    );

    page.addEventListener(
      'touchstart',
      (event) => {
        if (!isMobileLayout()) return;
        if (event.touches.length !== 1) return;

        const target = event.target;
        if (!(target instanceof Element)) return;
        if (shouldIgnoreTarget(target)) return;

        stopInertia();
        inertiaVelocity = 0;

        const touch = event.touches[0];
        isTracking = true;
        gestureAxis = '';
        startX = touch.clientX;
        startY = touch.clientY;
        lastX = touch.clientX;
        lastY = touch.clientY;
      },
      { passive: true }
    );

    page.addEventListener(
      'touchmove',
      (event) => {
        if (!isMobileLayout()) return;
        if (!isTracking) return;
        if (event.touches.length !== 1) return;

        const touch = event.touches[0];
        const deltaXFromStart = touch.clientX - startX;
        const deltaYFromStart = touch.clientY - startY;

        if (!gestureAxis) {
          const moveDistance = Math.abs(deltaXFromStart) + Math.abs(deltaYFromStart);
          if (moveDistance < GESTURE_START_THRESHOLD) return;

          const absDeltaX = Math.abs(deltaXFromStart);
          const absDeltaY = Math.abs(deltaYFromStart);

          gestureAxis = absDeltaY > absDeltaX * VERTICAL_GESTURE_RATIO
            ? 'vertical'
            : 'horizontal';
        }

        let rawDelta = 0;
        if (gestureAxis === 'vertical') {
          const deltaY = touch.clientY - lastY;
          lastY = touch.clientY;
          if (deltaY === 0) return;
          rawDelta = -deltaY * TOUCH_SCROLL_MULTIPLIER;
        } else {
          const deltaX = touch.clientX - lastX;
          lastX = touch.clientX;
          if (deltaX === 0) return;
          rawDelta = -deltaX * HORIZONTAL_SCROLL_MULTIPLIER;
        }

        const maxLeft = page.scrollWidth - page.clientWidth;
        const delta = clamp(rawDelta, -MAX_TOUCH_STEP, MAX_TOUCH_STEP);
        const nextLeft = Math.min(maxLeft, Math.max(0, page.scrollLeft + delta));
        if (nextLeft === page.scrollLeft) return;

        event.preventDefault();
        page.scrollLeft = nextLeft;
        inertiaVelocity = inertiaVelocity === 0
          ? delta
          : (inertiaVelocity * (1 - VELOCITY_SMOOTHING)) + (delta * VELOCITY_SMOOTHING);
      },
      { passive: false }
    );

    const reset = () => {
      if (!isMobileLayout()) {
        isTracking = false;
          gestureAxis = '';
        stopInertia();
        inertiaVelocity = 0;
        return;
      }

        const shouldGlide = isTracking && Boolean(gestureAxis);
      isTracking = false;
        gestureAxis = '';

      if (!shouldGlide) return;
      stopInertia();
      lastInertiaFrame = 0;
      inertiaRafId = window.requestAnimationFrame(runInertia);
    };

    page.addEventListener('touchend', reset, { passive: true });
    page.addEventListener('touchcancel', reset, { passive: true });
  };

  const setupDragScroll = (page) => {
    let isDown = false;
    let startX = 0;
    let startScroll = 0;

    page.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      // Do not start drag-scroll when interacting with UI controls.
      if (target.closest('a, button, input, textarea, select, label, [data-menu-drawer], [data-menu-toggle], [data-menu-close]')) {
        return;
      }

      isDown = true;
      startX = event.clientX;
      startScroll = page.scrollLeft;
      page.setPointerCapture(event.pointerId);
      page.classList.add('is-dragging');
    });

    page.addEventListener('pointermove', (event) => {
      if (!isDown) return;
      const delta = event.clientX - startX;
      page.scrollLeft = startScroll - delta;
    });

    const reset = () => {
      isDown = false;
      page.classList.remove('is-dragging');
    };

    page.addEventListener('pointerup', reset);
    page.addEventListener('pointercancel', reset);
    page.addEventListener('lostpointercapture', reset);

    // Images start a native drag-and-drop that fights the drag-scroll.
    page.addEventListener('dragstart', (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('img')) {
        event.preventDefault();
      }
    });
  };

  const setupKeyboard = (page) => {
    page.addEventListener('keydown', (event) => {
      if (isMobileLayout()) return;

      const step = Math.max(240, page.clientWidth * 0.6);
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        page.scrollBy({ left: step, behavior: 'smooth' });
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        page.scrollBy({ left: -step, behavior: 'smooth' });
      }
    });
  };

  const setupMenu = (page) => {
    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) return;

    const toggle = header.querySelector(MENU_TOGGLE_SELECTOR);
    const drawer = header.querySelector(MENU_DRAWER_SELECTOR);
    const close = header.querySelector(MENU_CLOSE_SELECTOR);
    if (!toggle || !drawer || !close) return;

    const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusables = () => Array.from(drawer.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden'
    );
    drawer.setAttribute('inert', '');

    // 国一覧のホイールを、後から登録される本文の慣性スクロールに渡さない（#190）。
    // 標準スクロールは止めず、一覧の overflow と overscroll-behavior に任せる。
    page.addEventListener('wheel', (event) => {
      if (header.classList.contains('is-open') && drawer.contains(event.target)) {
        event.stopImmediatePropagation();
      }
    }, { passive: true, capture: true });

    const getThemeColorMeta = () => {
      let meta = document.querySelector('meta[name="theme-color"]');
      if (meta instanceof HTMLMetaElement) return meta;

      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.append(meta);
      return meta;
    };

    const themeColorMeta = getThemeColorMeta();
    const initialThemeColor = themeColorMeta.getAttribute('content') || '';
    let currentThemeColor = initialThemeColor;
    const rootStyles = getComputedStyle(document.documentElement);
    const menuThemeColor = rootStyles.getPropertyValue('--menu-ui-bg').trim() || '#877e7f';

    const setThemeColor = (value) => {
      if (!value || value === currentThemeColor) return;
      themeColorMeta.setAttribute('content', value);
      currentThemeColor = value;
    };

    const syncThemeColorToPage = () => {
      const rootBackground = getComputedStyle(document.body).backgroundColor;
      if (!rootBackground) return;
      setThemeColor(rootBackground);
    };

    const drawerRevealTargets = Array.from(
      drawer.querySelectorAll('[data-element-reveal]')
    ).filter((target) => target instanceof HTMLElement);

    const resetDrawerReveal = () => {
      drawerRevealTargets.forEach((target) => {
        target.classList.remove('is-visible');
      });
    };

    const isTransparentHeader = header.classList.contains('c-header__transparent');
    const imageUrlToBlack = (url) => {
      if (!url) return url;
      if (/_black(?=\.[^./?#]+(?:[?#].*)?$)/.test(url)) return url;
      return url.replace(/(\.[^./?#]+)([?#].*)?$/, '_black$1$2');
    };
    const imageUrlFromBlack = (url) => {
      if (!url) return url;
      return url.replace(/_black(?=\.[^./?#]+(?:[?#].*)?$)/, '');
    };
    const toggleSrcsetVariant = (srcset, useBlack) => {
      if (!srcset) return srcset;

      return srcset
        .split(',')
        .map((item) => {
          const candidate = item.trim();
          if (!candidate) return candidate;

          const parts = candidate.split(/\s+/);
          const url = parts[0];
          const descriptor = parts.slice(1).join(' ');
          const nextUrl = useBlack ? imageUrlToBlack(url) : imageUrlFromBlack(url);

          return descriptor ? `${nextUrl} ${descriptor}` : nextUrl;
        })
        .join(', ');
    };
    const syncTransparentHeaderAssets = (useBlack) => {
      if (!isTransparentHeader) return;

      header.querySelectorAll('.c-header__fixed img').forEach((image) => {
        const currentSrc = image.getAttribute('src');
        if (!currentSrc) return;
        image.setAttribute('src', useBlack ? imageUrlToBlack(currentSrc) : imageUrlFromBlack(currentSrc));
      });

      header.querySelectorAll('.c-header__fixed source').forEach((source) => {
        const currentSrcset = source.getAttribute('srcset');
        if (!currentSrcset) return;
        source.setAttribute('srcset', toggleSrcsetVariant(currentSrcset, useBlack));
      });
    };

    const playDrawerReveal = () => {
      drawerRevealTargets.forEach((target) => {
        target.classList.add('js-reveal-item');
      });

      // Force style flush so re-adding visibility reliably retriggers transitions.
      void drawer.offsetWidth;

      drawerRevealTargets.forEach((target) => {
        target.classList.add('is-visible');
      });
    };

    const setOpen = (isOpen) => {
      header.classList.toggle('is-open', isOpen);
      toggle.setAttribute('aria-expanded', String(isOpen));
      drawer.setAttribute('aria-hidden', String(!isOpen));
      document.body.style.overflow = isOpen ? 'hidden' : '';
      const shouldTintBrowserUi = isOpen && isMobileLayout();
      document.documentElement.classList.toggle('is-menu-open', shouldTintBrowserUi);
      document.body.classList.toggle('is-menu-open', shouldTintBrowserUi);

      if (isOpen) {
        syncTransparentHeaderAssets(false);
        if (shouldTintBrowserUi) {
          setThemeColor(menuThemeColor);
        }
        playDrawerReveal();
        drawer.removeAttribute('inert');
        const first = focusables()[0];
        if (first) first.focus();
      } else {
        syncTransparentHeaderAssets(true);
        resetDrawerReveal();
        syncThemeColorToPage();
        drawer.setAttribute('inert', '');
        toggle.focus();
      }
    };

    syncThemeColorToPage();
    syncTransparentHeaderAssets(true);

    toggle.addEventListener('click', () => {
      const next = !header.classList.contains('is-open');
      setOpen(next);
    });

    close.addEventListener('click', () => setOpen(false));

    drawer.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href');
        if (!href || href === '#') return;

        const target = document.getElementById(href.slice(1));
        if (!(target instanceof HTMLElement)) return;

        event.preventDefault();
        setOpen(false);

        scrollToHashTarget(page, href);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (!header.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }

      // 下層と同じく、表示中の項目だけでドロワー内を巡回する（#190）。
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !drawer.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !drawer.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  /* ページ内リンクのスクロール先。TOPは横スクロールなので left を出す。
     offsetLeft は offsetParent 基準で入れ子の影響を受けるため、
     スクロール要素との差分から求める（#78 レビュー・#6） */
  const scrollToHashTarget = (page, href) => {
    if (!href || href === '#') return false;

    const target = document.getElementById(href.slice(1));
    if (!(target instanceof HTMLElement)) return false;

    window.requestAnimationFrame(() => {
      const left =
        target.getBoundingClientRect().left - page.getBoundingClientRect().left + page.scrollLeft;
      page.scrollTo({ left, top: 0, behavior: 'smooth' });
    });

    return true;
  };

  /* 目次のページ内リンク。ドロワー内は setupMenu 側で閉じる処理と一緒に扱う。
     文書全体の a[href^="#"] に広げないのは、Pipeline 既存のスキップリンク
     #MainContent（キーボード利用者の本文スキップ）を奪うため（#78 レビュー・#6） */
  const HASH_LINK_SCOPES = '.p-overview__toc, .c-footer__toc';

  const setupHashLinks = (page) => {
    document.querySelectorAll(HASH_LINK_SCOPES).forEach((scope) => {
      scope.querySelectorAll('a[href^="#"]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (!scrollToHashTarget(page, link.getAttribute('href'))) return;
          event.preventDefault();
        });
      });
    });
  };

  const setupBackToTop = (page) => {
    const links = document.querySelectorAll(PAGE_TOP_LINK_SELECTOR);
    if (!links.length) return;

    links.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        page.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
      });
    });
  };

  const setupDynamicBackground = (page) => {
    const root = document.documentElement;
    const styles = getComputedStyle(root);
    const base1 = parseHexColor(styles.getPropertyValue('--bg-base-1'));
    const base2 = parseHexColor(styles.getPropertyValue('--bg-base-2'));
    const base3 = parseHexColor(styles.getPropertyValue('--bg-base-3'));
    if (!base1 || !base2 || !base3) return;

    const overviewSection = page.querySelector('.p-overview[data-hs-section]');
    const perspective01 = page.querySelector('.p-perspective--01[data-hs-section]');
    const perspective02 = page.querySelector('.p-perspective--02[data-hs-section]');
    const background03 = page.querySelector('.p-hs__background--03[data-hs-background]');
    if (!(overviewSection instanceof HTMLElement)) return;
    if (!(perspective01 instanceof HTMLElement)) return;
    if (!(perspective02 instanceof HTMLElement)) return;
    if (!(background03 instanceof HTMLElement)) return;

    const COLOR_BLEND_STEPS = 20;
    let lastAppliedColorKey = '';

    const setBackgroundColor = (color) => {
      const colorKey = `${color.r},${color.g},${color.b}`;
      if (colorKey === lastAppliedColorKey) return;

      const rgb = toRgbString(color);
      root.style.setProperty('--bg-dynamic-start', rgb);
      root.style.setProperty('--bg-dynamic-mid', rgb);
      root.style.setProperty('--bg-dynamic-end', rgb);
      lastAppliedColorKey = colorKey;
    };

    const resolveColor = (scrollLeft) => {
      const transitionDistance = Math.max(1, page.clientWidth * 0.42);
      const triggers = [
        {
          // Slight delay from the very start, then settle to base-2 while overview is shown.
          point: Math.max(1, overviewSection.offsetLeft + page.clientWidth * 0.08),
          color: base2
        },
        {
          // Perspective 01 and 02 zone.
          point: perspective01.offsetLeft,
          color: base3
        },
        {
          // Switch earlier so color changes between section 02 and 03.
          point: perspective02.offsetLeft + perspective02.offsetWidth * 0.6,
          color: base1
        },
        {
          // From background--03 and onward.
          point: background03.offsetLeft,
          color: base2
        }
      ];

      let currentColor = base1;

      for (let index = 0; index < triggers.length; index += 1) {
        const start = Math.max(0, triggers[index].point - transitionDistance / 2);
        const end = triggers[index].point + transitionDistance / 2;

        if (scrollLeft < start) {
          return currentColor;
        }

        if (scrollLeft <= end) {
          const progress = clamp((scrollLeft - start) / Math.max(1, end - start), 0, 1);
          const easedProgress = easeInOutSine(progress);
          const quantizedProgress = Math.round(easedProgress * COLOR_BLEND_STEPS) / COLOR_BLEND_STEPS;
          return mixColor(currentColor, triggers[index].color, quantizedProgress);
        }

        currentColor = triggers[index].color;
      }

      return currentColor;
    };

    const updateBackground = () => {
      const scrollLeft = page.scrollLeft;
      const resolvedColor = resolveColor(scrollLeft);
      setBackgroundColor(resolvedColor);
    };

    let isTicking = false;
    const requestUpdate = () => {
      if (isTicking) return;
      isTicking = true;
      window.requestAnimationFrame(() => {
        isTicking = false;
        updateBackground();
      });
    };

    page.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);
    requestUpdate();
  };

  const setupPerspectiveFadeStage = (page) => {
    const stage = document.querySelector(PERSPECTIVE_STAGE_SELECTOR);
    if (!(stage instanceof HTMLElement)) return;

    const sourceSections = PERSPECTIVE_STAGE_SOURCE_SELECTORS
      .map((selector) => page.querySelector(selector))
      .filter((section) => section instanceof HTMLElement);

    if (!sourceSections.length) return;

    const panels = sourceSections.map((section, index) => {
      const panel = section.cloneNode(true);
      panel.classList.add('p-perspective-stage__panel');
      if (index !== 0) {
        panel.classList.add('is-stage-reveal-pending');
      }
      panel.setAttribute('data-stage-index', String(index));

      if (index !== 0) {
        // Ensure stage panels start from hidden reveal state.
        panel.querySelectorAll('.is-visible').forEach((target) => {
          target.classList.remove('is-visible');
        });
      } else {
        // Section 01 should not replay on fixed-start.
        panel.querySelectorAll('.js-reveal-image, .js-reveal-item, .js-reveal-text').forEach((target) => {
          target.classList.add('is-visible');
        });
      }

      stage.appendChild(panel);
      return panel;
    });
    const lastSourceSection = sourceSections[sourceSections.length - 1];

    const playedPanels = new WeakSet();
    const STAGE_REVEAL_BASE_DELAY = 140;
    const nextBackground = document.querySelector('.p-hs__background--03[data-hs-background]');
    const nextBackgroundContent = nextBackground instanceof HTMLElement
      ? nextBackground.querySelector('.p-collection')
      : null;

    const clearNextBackgroundOffset = () => {
      if (nextBackground instanceof HTMLElement) {
        nextBackground.style.transform = '';
      }
      if (nextBackgroundContent instanceof HTMLElement) {
        nextBackgroundContent.style.transform = '';
      }
    };
    const replayPanelReveal = (panel) => {
      if (playedPanels.has(panel)) return;
      playedPanels.add(panel);

      const images = Array.from(panel.querySelectorAll('.js-reveal-image'));
      const elements = Array.from(panel.querySelectorAll('.js-reveal-item'));
      const texts = Array.from(panel.querySelectorAll('.js-reveal-text'));

      [...images, ...elements, ...texts].forEach((target) => {
        target.classList.remove('is-visible');
      });

      void panel.offsetWidth;

      window.setTimeout(() => {
        panel.classList.remove('is-stage-reveal-pending');

        images.forEach((target, index) => {
          window.setTimeout(() => target.classList.add('is-visible'), 40 + index * 70);
        });

        elements.forEach((target, index) => {
          window.setTimeout(() => target.classList.add('is-visible'), 80 + index * 50);
        });

        texts.forEach((target, index) => {
          window.setTimeout(() => target.classList.add('is-visible'), 140 + index * 80);
        });
      }, STAGE_REVEAL_BASE_DELAY);
    };

    const forcePanelVisible = (panel) => {
      panel.classList.remove('is-stage-reveal-pending');
      panel.querySelectorAll('.js-reveal-image, .js-reveal-item, .js-reveal-text').forEach((target) => {
        target.classList.add('is-visible');
      });
    };

    const showPanelImmediately = (panel) => {
      panel.classList.remove('is-stage-reveal-pending');
      panel.querySelectorAll('.js-reveal-image, .js-reveal-item, .js-reveal-text').forEach((target) => {
        target.classList.add('is-visible');
      });
    };

    const revealSourceSection = (section) => {
      if (!(section instanceof HTMLElement)) return;

      section.querySelectorAll('.js-reveal-image').forEach((target) => {
        target.classList.add('is-visible');
      });

      section.querySelectorAll('[data-element-reveal]').forEach((target) => {
        target.classList.add('js-reveal-item');
        target.classList.add('is-visible');
      });

      section.querySelectorAll('.js-reveal-text').forEach((target) => {
        target.classList.add('is-visible');
      });
    };

    const HOLD_RATIO = 0.48;
    const FADE_OUT_END_RATIO = 0.7;
    const BLANK_END_RATIO = 0.78;
    const LAST_PANEL_RELEASE_OFFSET_RATIO = 0.01;
    const LAST_PANEL_RELEASE_OFFSET_MIN = 80;

    const clearStageReleaseOffset = () => {
      stage.style.transform = '';
    };

    const updateStage = () => {
      if (isPerspectiveFixedDisabled() || isMobileLayout()) {
        document.documentElement.classList.remove('is-perspective-stage-active');
        clearNextBackgroundOffset();
        clearStageReleaseOffset();
        panels.forEach((panel) => {
          panel.style.opacity = '0';
          panel.classList.remove('is-active');
        });
        return;
      }

      const first = sourceSections[0];
      const startAnchorIndex = Math.min(
        sourceSections.length - 1,
        Math.max(0, PERSPECTIVE_STAGE_START_SECTION_INDEX)
      );
      const startAnchor = sourceSections[startAnchorIndex] || first;
      const last = sourceSections[sourceSections.length - 1];
      const fixedStartOffset = 20;
      const fixedEndOffset = 20;
      const start = Math.max(0, startAnchor.offsetLeft + startAnchor.offsetWidth / 2 - page.clientWidth / 2 - fixedStartOffset);
      const releaseOffset = Math.max(
        LAST_PANEL_RELEASE_OFFSET_MIN,
        page.clientWidth * LAST_PANEL_RELEASE_OFFSET_RATIO
      );
      const baseEnd = last.offsetLeft + last.offsetWidth / 2 - page.clientWidth / 2;
      const end = Math.max(start + 1, baseEnd + releaseOffset - fixedEndOffset);
      const scrollLeft = page.scrollLeft;
      const isActive = scrollLeft >= start && scrollLeft <= end;

      clearNextBackgroundOffset();

      document.documentElement.classList.toggle('is-perspective-stage-active', isActive);
      if (!isActive) {
        clearStageReleaseOffset();

        if (scrollLeft > end) {
          revealSourceSection(lastSourceSection);
        }

        panels.forEach((panel) => {
          panel.style.opacity = '0';
          panel.classList.remove('is-active');
        });
        return;
      }

      if (panels.length === 1) {
        panels[0].style.opacity = '1';
        panels[0].classList.add('is-active');
        showPanelImmediately(panels[0]);
        return;
      }

      const stageSpan = Math.max(1, end - start);
      const progress = clamp((scrollLeft - start) / stageSpan, 0, 1);
      const segmentLength = 1 / panels.length;
      const segmentIndex = Math.min(
        panels.length - 1,
        Math.floor(progress / Math.max(0.0001, segmentLength))
      );
      const segmentStart = segmentIndex * segmentLength;
      const segmentEnd = Math.min(1, segmentStart + segmentLength);
      const segmentSpan = Math.max(0.0001, segmentEnd - segmentStart);
      const localProgress = clamp((progress - segmentStart) / segmentSpan, 0, 1);
      const isLastSegment = segmentIndex === panels.length - 1;

      // Keep stage fully fixed until deactivation to avoid a brief left drift.
      clearStageReleaseOffset();

      if (isLastSegment) {
        revealSourceSection(lastSourceSection);
      }

      let currentOpacity = 1;
      let nextOpacity = 0;

      if (segmentIndex < panels.length - 1) {
        if (localProgress <= HOLD_RATIO) {
          currentOpacity = 1;
          nextOpacity = 0;
        } else if (localProgress <= FADE_OUT_END_RATIO) {
          const fadeOutProgress = clamp(
            (localProgress - HOLD_RATIO) / Math.max(0.0001, FADE_OUT_END_RATIO - HOLD_RATIO),
            0,
            1
          );
          currentOpacity = 1 - fadeOutProgress;
          nextOpacity = 0;
        } else if (localProgress <= BLANK_END_RATIO) {
          // Keep both panels hidden for a short beat between sections.
          currentOpacity = 0;
          nextOpacity = 0;
        } else {
          const fadeInProgress = clamp(
            (localProgress - BLANK_END_RATIO) / Math.max(0.0001, 1 - BLANK_END_RATIO),
            0,
            1
          );
          currentOpacity = 0;
          nextOpacity = fadeInProgress;
        }
      }

      panels.forEach((panel, index) => {
        let opacity = 0;

        if (index === segmentIndex) opacity = currentOpacity;
        if (index === segmentIndex + 1) opacity = nextOpacity;

        panel.style.opacity = String(opacity);
        panel.classList.toggle('is-active', opacity > 0.01);

        if (opacity > 0.01) {
          if (index === 0) {
            showPanelImmediately(panel);
          } else {
            replayPanelReveal(panel);
          }
        }
      });
    };

    let isTicking = false;
    const requestUpdate = () => {
      if (isTicking) return;
      isTicking = true;
      window.requestAnimationFrame(() => {
        isTicking = false;
        updateStage();
      });
    };

    page.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);
    requestUpdate();
  };

  const setupPerspectiveHeadingRelease = (page) => {
    const background = page.querySelector('.p-hs__background[data-hs-background]');
    const intro = background
      ? Array.from(background.children).find(
          (child) => child instanceof HTMLElement && child.classList.contains('p-perspective__intro')
        )
      : null;
    const section04 = page.querySelector('.p-perspective--04[data-hs-section]');

    if (!(background instanceof HTMLElement)) return;
    if (!(intro instanceof HTMLElement)) return;
    if (!(section04 instanceof HTMLElement)) return;

    const getAbsoluteLeft = (element) => {
      const pageRect = page.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      return page.scrollLeft + (rect.left - pageRect.left);
    };

    const RELEASE_ADVANCE_RATIO = 0.05;
    const RELEASE_SHIFT_MULTIPLIER = 1.35;
    const RELEASE_DEAD_ZONE_RATIO = 0.04;
    const getReleasePoint = () => {
      const advance = page.clientWidth * RELEASE_ADVANCE_RATIO;
      return Math.max(0, getAbsoluteLeft(section04) - advance);
    };

    const getReleaseDeadZone = () => Math.max(24, page.clientWidth * RELEASE_DEAD_ZONE_RATIO);

    const updateRelease = () => {
      const scrollLeft = page.scrollLeft;
      const releasePoint = getReleasePoint();
      const baseShift = scrollLeft - releasePoint;
      const effectiveShift = Math.max(0, baseShift - getReleaseDeadZone());
      const shift = effectiveShift * RELEASE_SHIFT_MULTIPLIER;
      const isReleased = shift > 0;

      document.documentElement.classList.toggle('is-perspective-heading-released', isReleased);
      intro.style.setProperty('--perspective-heading-release-shift', `${shift}px`);
    };

    let isTicking = false;
    const requestUpdate = () => {
      if (isTicking) return;
      isTicking = true;
      window.requestAnimationFrame(() => {
        isTicking = false;
        updateRelease();
      });
    };

    page.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);
    requestUpdate();
  };

  const init = () => {
    const page = document.querySelector(PAGE_SELECTOR);
    if (!page) return;

    setupMenu(page);
    setupWheelHorizontal(page);
    setupTouchVerticalHorizontal(page);
    setupDragScroll(page);
    setupKeyboard(page);
    setupBackToTop(page);
    setupHashLinks(page);
    setupDynamicBackground(page);
    setupPerspectiveFadeStage(page);
    setupPerspectiveHeadingRelease(page);
  };

  window.addEventListener('DOMContentLoaded', init);
})();
